import {
  AccountFilterAdminColumns,
  AccountFilterColumns,
  AccountFilterModel,
  useAccountFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useSetAccountFilter,
  useValidateAccountListOrder,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { array, string, object as yupObject } from 'yup';
import { useListCount, useSlideLayoutSafe } from '../slide-layout';
import {
  FilterScreenLayout,
  FilterScreenLayoutProps,
} from './filterScreenLayout';
import { ACCOUNT_SECTIONS, AccountSection } from './filterSectionTypes';

const formSchema = yupObject({
  keyword: string().optional().nullable(),
  columns: array().of(string().optional().nullable()).optional().nullable(),
  groups: array().of(string().optional().nullable()).optional().nullable(),
  roles: array().of(string().optional().nullable()).optional().nullable(),
});

const DEFAULT_ACCOUNT_COLUMNS = new AccountFilterModel().columns ?? [];

export type AccountFilterScreenHandle = {
  onReset: () => Promise<void>;
};

export type AccountFilterScreenProps = {
  mode: 'page' | 'modal';
  onBack?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
  isInWorkspace: boolean;
  bottomBar?: FilterScreenLayoutProps['bottomBar'];
};

const COLUMNS_ONLY_SECTIONS: readonly AccountSection[] = ['columns'] as const;

const ACCOUNT_COLUMNS_I18N_PREFIX = 'account.filter';

export const AccountFilterScreen = forwardRef<
  AccountFilterScreenHandle,
  AccountFilterScreenProps
>(function AccountFilterScreen(
  {
    mode,
    onBack,
    onDismiss,
    isVisible,
    isInWorkspace,
    bottomBar,
  }: AccountFilterScreenProps,
  ref
) {
  const { t } = useTranslation();
  const slideLayout = useSlideLayoutSafe();
  const listCount = useListCount();
  const { data: filter } = useAccountFilter();
  const { mutateAsync: setAccountFilter } = useSetAccountFilter();
  const validateListOrder = useValidateAccountListOrder();
  const currentWorkspace = useCurrentWorkspace(true);
  const { account } = useCurrentAccount();

  const isGlobalAdmin = account?.isGlobalAdmin?.() || false;
  const workspaceId = currentWorkspace?.id;
  const filterTitle = t('common.filter');

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      keyword: filter?.keyword || '',
      columns: filter?.columns ?? DEFAULT_ACCOUNT_COLUMNS,
      groups: filter?.groups ?? [],
      roles: filter?.roles ?? [],
    },
  });

  const { control, reset, watch } = formMethods;
  const formData = watch();

  useEffect(() => {
    slideLayout?.setColumnTitle(filterTitle);
  }, [slideLayout, filterTitle]);

  useEffect(() => {
    slideLayout?.setColumnCount(listCount);
  }, [slideLayout, listCount]);

  useEffect(() => {
    return () => {
      slideLayout?.setColumnTitle(undefined);
      slideLayout?.setColumnCount(undefined);
    };
  }, [slideLayout]);

  const resetForm = useCallback(() => {
    reset({
      keyword: '',
      columns: DEFAULT_ACCOUNT_COLUMNS,
      groups: [],
      roles: [],
    });
  }, [reset]);

  useEffect(() => {
    if (!filter) resetForm();
  }, [filter, resetForm]);

  const onCloseAfterApply = mode === 'page' ? onBack : onDismiss;

  const onSubmit = async (data: any) => {
    if (!data?.keyword?.length) delete data.keyword;
    const filterModel = new AccountFilterModel({ ...filter, ...data });
    await Promise.all([
      setAccountFilter(filterModel),
      validateListOrder(filterModel),
    ]);
    onCloseAfterApply?.();
  };

  const onReset = useCallback(async () => {
    resetForm();
    const defaultFilter = new AccountFilterModel();
    await Promise.all([
      setAccountFilter(defaultFilter),
      validateListOrder(defaultFilter),
    ]);
  }, [resetForm, setAccountFilter, validateListOrder]);

  useImperativeHandle(ref, () => ({ onReset }), [onReset]);

  const canReset = (): boolean => {
    return Boolean(
      (formData.keyword && formData.keyword.length > 0) ||
        (formData.groups && formData.groups.length > 0) ||
        (formData.roles && formData.roles.length > 0) ||
        JSON.stringify([...(formData.columns ?? [])].sort()) !==
          JSON.stringify([...DEFAULT_ACCOUNT_COLUMNS].sort())
    );
  };

  return (
    <FilterScreenLayout
      mode={mode}
      onBack={onBack}
      onDismiss={onDismiss}
      isVisible={isVisible}
      formMethods={formMethods}
      resultsCount={listCount ?? 0}
      viewResultsTranslationKey="account.filter.viewResults"
      onReset={onReset}
      canReset={canReset}
      onSubmit={onSubmit}
      variant="account"
      sections={isInWorkspace ? ACCOUNT_SECTIONS : COLUMNS_ONLY_SECTIONS}
      control={control}
      workspaceId={workspaceId}
      isGlobalAdmin={isGlobalAdmin}
      columnsConfig={{
        baseColumns: AccountFilterColumns,
        adminColumns: !isInWorkspace ? AccountFilterAdminColumns : undefined,
        i18nPrefix: ACCOUNT_COLUMNS_I18N_PREFIX,
      }}
      bottomBar={bottomBar}
    />
  );
});
